import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { accountTypeAPI } from "api/Accounts/AccountsAPI";
import { AccountType } from "api/Accounts/AccountsInterfaces";
import { AccountTypeCard } from "./AccountTypeCard";

interface AccountTypeCarouselProps {
  onSelect: (accountType: AccountType) => void;
  excludeIds?: number[];
  title?: string;
  selectedId?: number | string | null;
  includeCallToActionButton?: boolean;
}

const CARD_WIDTH = 340;
const CARD_GAP = 24;

const AccountTypeCarousel: React.FC<AccountTypeCarouselProps> = ({
  onSelect,
  excludeIds = [],
  title = "",
  selectedId: externalSelectedId = null,
  includeCallToActionButton = true,
}) => {
  const [accountTypes, setAccountTypes] = useState<AccountType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (externalSelectedId !== null && externalSelectedId !== undefined) {
      setSelectedId(Number(externalSelectedId));
    }
  }, [externalSelectedId]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const list = await accountTypeAPI.getAll();

        const currentId =
          externalSelectedId !== null && externalSelectedId !== undefined
            ? Number(externalSelectedId)
            : null;

        const filtered = list.filter((at) => {
          if (currentId !== null && at.id === currentId) return true;
          return !excludeIds.includes(at.id);
        });

        setAccountTypes(filtered);
      } catch (e) {
        console.error("Failed to load account types:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchTypes();
  }, [excludeIds, externalSelectedId]);

  const handleSelect = (acct: AccountType) => {
    onSelect(acct);
  };

  const scrollByCards = (dir: number) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: dir * (CARD_WIDTH + CARD_GAP),
      behavior: "smooth",
    });
  };

  const hasTitle = Boolean(title?.trim());

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        mt: 2,
      }}
    >
      {hasTitle && (
        <Typography
          variant="h4"
          textAlign="center"
          sx={{ mb: 4, color: "#fcfaf6", fontWeight: 700 }}
        >
          {title}
        </Typography>
      )}

      <IconButton
        onClick={() => scrollByCards(-1)}
        sx={{
          position: "absolute",
          left: 8,
          top: hasTitle ? "58%" : "50%",
          transform: "translateY(-50%)",
          zIndex: 10,
          backgroundColor: "rgba(0,0,0,0.3)",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.5)" },
        }}
      >
        <ArrowBackIosNewIcon sx={{ color: "white" }} />
      </IconButton>

      <Box
        ref={scrollRef}
        sx={{
          overflowX: "auto",
          overflowY: "visible",
          scrollBehavior: "smooth",
          scrollSnapType: "x mandatory",
          px: 6,
          scrollPaddingLeft: 48,
          scrollPaddingRight: 48,

          // optional: hide scrollbar without disabling scroll
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: `${CARD_GAP}px`,
            width: "max-content",
            alignItems: "stretch",
          }}
        >
          {accountTypes.map((acct) => {
            const isSelected =
              selectedId !== null && Number(selectedId) === Number(acct.id);

            return (
              <Box
                key={acct.id}
                sx={{
                  scrollSnapAlign: "start",
                  flex: "0 0 auto",
                  width: CARD_WIDTH,
                }}
              >
                <AccountTypeCard
                  id={acct.id}
                  name={acct.name}
                  priceMonthly={Number(acct.price_per_month)}
                  description={acct.description}
                  features={acct.features}
                  selected={isSelected}
                  onSelect={() => handleSelect(acct)}
                  includeCallToActionButton={includeCallToActionButton}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      <IconButton
        onClick={() => scrollByCards(1)}
        sx={{
          position: "absolute",
          right: 8,
          top: hasTitle ? "58%" : "50%",
          transform: "translateY(-50%)",
          zIndex: 10,
          backgroundColor: "rgba(0,0,0,0.3)",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.5)" },
        }}
      >
        <ArrowForwardIosIcon sx={{ color: "white" }} />
      </IconButton>
    </Box>
  );
};

export default AccountTypeCarousel;